import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdSenseConfig } from '../hooks/useAdSenseConfig';
import '../styles/components/GoogleAdsense.css';

/**
 * GoogleAdsense コンポーネント
 *
 * 管理画面 (/admin/affiliate ウィジェット設定) の clientId/slotId/adFormat/showForPlans
 * を useAdSenseConfig で取得し、フォールバックとして prop の slotId と
 * デフォルトのクライアントIDを使用する。
 */

// レガシー固定値（管理画面で設定されていない場合のフォールバック）
const FALLBACK_CLIENT_ID = 'ca-pub-1859127931362916';
const FALLBACK_SLOT_ID = '1093400567';

// 表示判定: enabled + プラン一致
const shouldShowAd = (config, plan) => {
  if (!config) return plan === 'free';  // 設定読み込み前は無料プランのみ
  if (config.enabled === false) return false;
  const allowedPlans = (config.showForPlans && config.showForPlans.length > 0)
    ? config.showForPlans
    : ['free'];
  return allowedPlans.includes(plan || 'free');
};

const GoogleAdsense = ({
  slotId: propSlotId,
  format: propFormat,
  style = {},
  responsive = true,
  className = ''
}) => {
  const { plan } = useAuth();
  const config = useAdSenseConfig();
  const adRef = useRef(null);
  const adInitialized = useRef(false);
  const adTimeoutRef = useRef(null);

  // 管理画面の値を優先、なければ prop、最後にフォールバック
  const clientId = config?.clientId || FALLBACK_CLIENT_ID;
  const slotId = config?.slotId || propSlotId || FALLBACK_SLOT_ID;
  const format = propFormat || config?.adFormat || 'auto';

  const visible = shouldShowAd(config, plan);

  useEffect(() => {
    if (!visible) return;
    if (!window.adsbygoogle) {
      console.warn('AdSense script not loaded');
      return;
    }
    if (adInitialized.current) return;

    adTimeoutRef.current = setTimeout(() => {
      try {
        if (adRef.current) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          adInitialized.current = true;
        }
      } catch (error) {
        console.error('AdSense initialization error:', error);
      }
    }, 100);

    return () => {
      if (adTimeoutRef.current) clearTimeout(adTimeoutRef.current);
    };
  }, [visible, slotId]);

  if (!visible) return null;

  // fluid フォーマットは「記事内広告」用なので data-ad-layout="in-article" を自動付与
  const isInArticle = format === 'fluid';

  return (
    <div className={`adsense-container ${className}`} ref={adRef}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', textAlign: isInArticle ? 'center' : undefined, ...style }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
        data-ad-format={format}
        {...(isInArticle ? { 'data-ad-layout': 'in-article' } : { 'data-full-width-responsive': responsive.toString() })}
        data-ad-test="off"
      />
    </div>
  );
};

/**
 * AdBanner - 固定サイズ Rectangle 広告
 * 管理画面に slotId が設定されていればそれを使用、なければ FALLBACK_SLOT_ID。
 */
export const AdBanner = ({ className = '' }) => {
  const { plan } = useAuth();
  const config = useAdSenseConfig();
  if (!shouldShowAd(config, plan)) return null;

  return (
    <div className={`ad-banner-wrapper ${className}`}>
      <div className="ad-banner-wrapper__label">広告</div>
      <GoogleAdsense
        slotId={config?.slotId || FALLBACK_SLOT_ID}
        format={config?.adFormat || 'rectangle'}
        responsive={false}
        className="adsbygoogle--rectangle"
      />
    </div>
  );
};

/**
 * AdBannerResponsive - 固定サイズ広告バナー（300x250 - Medium Rectangle）
 * 旧実装は ins タグを自前で出していたが、GoogleAdsense に統一。
 */
export const AdBannerResponsive = ({ className = '' }) => {
  const { plan } = useAuth();
  const config = useAdSenseConfig();
  const adRef = useRef(null);
  const adInitialized = useRef(false);

  const clientId = config?.clientId || FALLBACK_CLIENT_ID;
  const slotId = config?.slotId || FALLBACK_SLOT_ID;
  const visible = shouldShowAd(config, plan);

  useEffect(() => {
    if (!visible) return;
    if (adInitialized.current) return;
    if (!window.adsbygoogle) return;

    const timer = setTimeout(() => {
      try {
        if (adRef.current) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          adInitialized.current = true;
        }
      } catch (e) {
        console.error('AdSense init error:', e);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [visible, slotId]);

  if (!visible) return null;

  // fluid（記事内）の場合は GoogleAdsense（自動で layout 付与）を使う
  const isInArticle = (config?.adFormat || '') === 'fluid';
  if (isInArticle) {
    return (
      <div className={`ad-banner-fixed ${className}`}>
        <div className="ad-banner-fixed__label">スポンサード</div>
        <GoogleAdsense
          format="fluid"
          responsive={false}
          className=""
        />
      </div>
    );
  }

  return (
    <div className={`ad-banner-fixed ${className}`} ref={adRef}>
      <div className="ad-banner-fixed__label">スポンサード</div>
      <ins
        className="adsbygoogle"
        style={{ display: 'inline-block', width: '300px', height: '250px' }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
      />
    </div>
  );
};

/**
 * SafeAdBannerResponsive - 広告が読み込めない場合、DOM自体を削除
 */
export const SafeAdBannerResponsive = ({ className = '' }) => {
  const { plan } = useAuth();
  const config = useAdSenseConfig();
  const containerRef = useRef(null);
  const adInitialized = useRef(false);
  const [visible, setVisible] = useState(true);

  const clientId = config?.clientId || FALLBACK_CLIENT_ID;
  const slotId = config?.slotId || FALLBACK_SLOT_ID;
  const shouldShow = shouldShowAd(config, plan);

  useEffect(() => {
    if (!shouldShow) return;

    if (!adInitialized.current && window.adsbygoogle) {
      const timer = setTimeout(() => {
        try {
          if (containerRef.current) {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            adInitialized.current = true;
          }
        } catch (e) {
          console.error('AdSense init error:', e);
        }
      }, 100);

      const checkTimer = setTimeout(() => {
        if (containerRef.current) {
          const ins = containerRef.current.querySelector('ins.adsbygoogle');
          const status = ins?.getAttribute('data-ad-status');
          if (!ins || status === 'unfilled') {
            setVisible(false);
          }
        }
      }, 1500);

      return () => {
        clearTimeout(timer);
        clearTimeout(checkTimer);
      };
    }
  }, [shouldShow, slotId]);

  if (!shouldShow || !visible) return null;

  // fluid（記事内）の場合は GoogleAdsense 経由（自動で layout 付与）
  const isInArticle = (config?.adFormat || '') === 'fluid';
  if (isInArticle) {
    return (
      <div ref={containerRef} className={`ad-banner-fixed ${className}`}>
        <div className="ad-banner-fixed__label">スポンサード</div>
        <GoogleAdsense
          format="fluid"
          responsive={false}
          className=""
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`ad-banner-fixed ${className}`}>
      <div className="ad-banner-fixed__label">スポンサード</div>
      <ins
        className="adsbygoogle"
        style={{ display: 'inline-block', width: '300px', height: '250px' }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
      />
    </div>
  );
};

export default GoogleAdsense;
